import React from 'react';
import { ArrowLeft, Phone, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ShareButton } from '@/components/ShareButton';
import { inline } from '@/components/MarkdownText';

// Line-based renderer (keeps the legacy look: one paragraph per line, blank line = spacing).
const renderArticleLine = (line, i) => {
  const trimmed = line.trim();
  if (!trimmed) return <br key={i} />;
  const img = trimmed.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
  if (img) {
    return (
      <figure key={i} className="my-4 -mx-5 sm:mx-0" data-testid="article-inline-image">
        <img src={img[2]} alt={img[1]} loading="lazy" className="w-full max-h-[420px] object-cover" />
        {img[1] && <figcaption className="text-[11px] text-gray-400 mt-1.5 px-5 sm:px-0">{inline(img[1], `cap-${i}`)}</figcaption>}
      </figure>
    );
  }
  if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4 && !trimmed.slice(2, -2).includes('**')) {
    return <h3 key={i} className="font-bold text-[#1D1D1F] text-base mt-4 mb-2">{inline(trimmed.slice(2, -2), i)}</h3>;
  }
  const h = trimmed.match(/^(#{1,3})\s+(.*)$/);
  if (h) return <h3 key={i} className={`font-bold text-[#1D1D1F] mt-4 mb-2 ${h[1].length === 1 ? 'text-lg' : 'text-base'}`}>{inline(h[2], i)}</h3>;
  if (trimmed.startsWith('> ')) {
    return <blockquote key={i} className="border-l-4 border-[#38B2AC] pl-4 py-1 my-3 text-gray-600 italic bg-[#38B2AC]/5" data-testid="article-quote">{inline(trimmed.slice(2), i)}</blockquote>;
  }
  if (trimmed.startsWith('- ')) {
    return <p key={i} className="pl-4 before:content-['•'] before:mr-2 before:text-[#38B2AC]">{inline(trimmed.slice(2), i)}</p>;
  }
  return <p key={i}>{inline(trimmed, i)}</p>;
};

export const ArticleDetail = ({ article, onBack }) => {
  const { language } = useLanguage();
  const getField = (obj, field) => obj[`${field}_${language}`] || obj[`${field}_es`] || '';
  const galleryImages = article.gallery_images?.filter(img => img && !img.includes('transparent')) || [];
  const credit = getField(article, 'image_credit');

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="article-detail">
      <div className="relative">
        {article.image && (
          <img src={article.image} alt={getField(article, 'title')} className="w-full h-56 object-cover" />
        )}
        <button onClick={onBack}
          className="absolute safe-top-offset left-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow" data-testid="article-back-btn">
          <ArrowLeft size={20} />
        </button>
        <ShareButton kind="a" itemId={article.id} variant="round" className="absolute safe-top-offset right-4" testId="share-article-btn" />
      </div>
      {article.image && credit && (
        <p className="px-5 pt-2 text-[11px] text-gray-400 max-w-3xl mx-auto" data-testid="article-image-credit">{inline(credit, 'credit')}</p>
      )}
      <div className="px-5 py-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-[#1D1D1F] mb-4">{getField(article, 'title')}</h1>
        <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-line" data-testid="article-content">
          {getField(article, 'content').split('\n').map(renderArticleLine)}
        </div>
        {galleryImages.length > 0 && (
          <div className="mt-6" data-testid="article-gallery">
            <h3 className="font-semibold text-[#1D1D1F] text-sm mb-3">
              {language === 'es' ? 'Galeria' : language === 'de' ? 'Galerie' : 'Gallery'}
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
              {galleryImages.map((img, i) => (
                <img key={i} src={img} alt="" className="w-48 h-32 object-cover flex-shrink-0" />
              ))}
            </div>
          </div>
        )}
        {article.phone && (
          <a href={`tel:${article.phone}`}
            className="flex items-center justify-center gap-2 mt-6 bg-red-500 text-white py-3 rounded-xl font-semibold" data-testid="call-emergency">
            <Phone size={18} />
            {language === 'es' ? 'Llamar al' : 'Call'} {article.phone}
          </a>
        )}
        {article.links?.length > 0 && (
          <div className="mt-6 space-y-2">
            <h3 className="font-semibold text-[#1D1D1F] text-sm mb-3">
              {language === 'es' ? 'Enlaces utiles' : language === 'de' ? 'Nutzliche Links' : 'Useful links'}
            </h3>
            {article.links.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 hover:border-[#38B2AC] transition-colors">
                <span className="text-sm font-medium text-gray-800">{link.name}</span>
                <ExternalLink size={16} className="text-gray-400" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleDetail;
